import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Loader2, Check, ExternalLink, ShieldCheck, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";

const short = (s) => (s ? `${s.slice(0, 6)}…${s.slice(-6)}` : "");

export const PaymentModal = ({ open, onOpenChange }) => {
  const [config, setConfig] = useState(null);
  const [sig, setSig] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open && !config) {
      api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
    }
    if (!open) {
      setTimeout(() => {
        setSig("");
        setError("");
        setResult(null);
        setLoading(false);
      }, 300);
    }
  }, [open, config]);

  const copyAddress = () => {
    if (!config) return;
    navigator.clipboard.writeText(config.wallet);
    setCopied(true);
    toast.success("Wallet address copied");
    setTimeout(() => setCopied(false), 1800);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(result.telegram_link);
    toast.success("Telegram link copied");
  };

  const verify = async () => {
    if (!sig.trim()) {
      setError("Paste your transaction signature first.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/payment/verify", { signature: sig.trim() });
      setResult(data);
      toast.success("Payment verified on-chain — welcome to 500X");
    } catch (e) {
      setError(e?.response?.data?.detail || "Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="solana-payment-modal"
        className="sm:max-w-md bg-[#0A0D14] border border-[#14f19533] text-white p-0 overflow-hidden"
      >
        <AnimatePresence mode="wait">
          {!result ? (
            <motion.div
              key="pay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, y: -8 }}
              className="p-6"
            >
              <DialogHeader>
                <DialogTitle className="font-display font-extrabold text-2xl text-white">
                  Pay 2 SOL to enter
                </DialogTitle>
                <DialogDescription className="text-[#94A3B8]">
                  Send exactly <span className="text-[#14F195] font-semibold">2 SOL</span> to the
                  wallet below, then paste your transaction signature to unlock the group.
                </DialogDescription>
              </DialogHeader>

              <div className="mt-6 flex justify-center">
                <div className="rounded-2xl bg-white p-3">
                  {config ? (
                    <QRCodeSVG
                      data-testid="solana-qr-code"
                      value={config.solana_pay_uri}
                      size={168}
                      bgColor="#ffffff"
                      fgColor="#05060A"
                      level="M"
                    />
                  ) : (
                    <div className="w-[168px] h-[168px] flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-[#05060A] animate-spin" />
                    </div>
                  )}
                </div>
              </div>
              <p className="mt-2 text-center font-mono text-[11px] text-[#64748B]">
                Scan with Phantom / Solflare · Solana Pay
              </p>

              <div className="mt-5">
                <label className="font-mono text-[10px] uppercase tracking-wider text-[#64748B]">
                  VIP Wallet Address
                </label>
                <button
                  data-testid="copy-wallet-address-button"
                  onClick={copyAddress}
                  className="mt-1.5 w-full flex items-center justify-between gap-3 rounded-xl border border-[#ffffff14] bg-[#05060A] px-4 py-3 hover:border-[#14f19544] transition-colors group"
                >
                  <span data-testid="solana-wallet-address" className="font-mono text-sm text-white truncate">
                    {config ? short(config.wallet) : "…"}
                  </span>
                  {copied ? (
                    <Check className="w-4 h-4 text-[#14F195] shrink-0" />
                  ) : (
                    <Copy className="w-4 h-4 text-[#64748B] group-hover:text-[#14F195] shrink-0 transition-colors" />
                  )}
                </button>
              </div>

              <div className="mt-4">
                <label className="font-mono text-[10px] uppercase tracking-wider text-[#64748B]">
                  Transaction Signature
                </label>
                <input
                  data-testid="tx-signature-input"
                  value={sig}
                  onChange={(e) => {
                    setSig(e.target.value);
                    setError("");
                  }}
                  placeholder="Paste your Solana tx signature…"
                  className="mt-1.5 w-full rounded-xl border border-[#ffffff14] bg-[#05060A] px-4 py-3 text-sm font-mono text-white placeholder:text-[#3f4a5c] focus:outline-none focus:border-[#14f19566] transition-colors"
                />
                {error && (
                  <p data-testid="payment-error" className="mt-2 text-sm text-[#FF5470]">
                    {error}
                  </p>
                )}
              </div>

              <button
                data-testid="verify-transaction-button"
                onClick={verify}
                disabled={loading}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#14F195] px-6 py-3.5 text-base font-bold text-[#05060A] hover:bg-[#3dffb0] disabled:opacity-60 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-95 transition-transform duration-200"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Verifying on-chain…
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-5 h-5" />
                    Verify & Unlock
                  </>
                )}
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="success"
              data-testid="telegram-invite-container"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-8 text-center grid-bg"
            >
              <div className="mx-auto inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#14f1951a] border border-[#14f19544] mb-5">
                <PartyPopper className="w-8 h-8 text-[#14F195]" />
              </div>
              <h3 className="font-display font-extrabold text-2xl text-white">
                You're in. Welcome to 500X.
              </h3>
              <p className="mt-2 text-sm text-[#94A3B8]">
                Verified {result.amount_sol?.toFixed?.(2) ?? "2.00"} SOL on-chain. Your private
                Telegram invite is below — don't share it.
              </p>

              <a
                data-testid="telegram-invite-link"
                href={result.telegram_link}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#14F195] px-6 py-4 text-base font-bold text-[#05060A] hover:bg-[#3dffb0] hover:scale-[1.02] active:scale-95 transition-transform duration-200"
              >
                <ExternalLink className="w-5 h-5" />
                Open Private Telegram
              </a>

              <button
                data-testid="copy-telegram-link-button"
                onClick={copyLink}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#ffffff1f] px-6 py-3 text-sm font-semibold text-white hover:border-[#14f19566] transition-colors"
              >
                <Copy className="w-4 h-4" />
                Copy invite link
              </button>

              <p className="mt-4 font-mono text-[11px] text-[#64748B] break-all">
                {result.telegram_link}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};
